import type { CatalogSearchItem, SearchType } from "@/types/common/filters";
import { searchTypesEnum } from "@/types/common/filters";
import type { RoleType } from "@/types/entities/roles";
import type { TagType } from "@/types/entities/tags";
import type { UserType } from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Icon,
} from "@elaniintc/goes-design-system";
import type { EntityType, FilterSearchProps } from "./FilterSearch.types";

interface DropdownContentProps {
  isLoading: boolean;
  _data: EntityType[];
  searchType: SearchType;
  selected: EntityType[] | [];
  handleSelectUser: (user: UserType, action?: "add" | "remove") => void;
  onUpdateSearch: FilterSearchProps["onUpdateSearch"];
  setIsOpen: (value: boolean) => void;
  hasNextPage: boolean;
  loaderRef: (node?: Element | null) => void;
}

export const DropdownContent = ({
  isLoading,
  _data,
  searchType,
  selected,
  handleSelectUser,
  onUpdateSearch,
  setIsOpen,
  hasNextPage,
  loaderRef,
}: DropdownContentProps) => {
  if (isLoading) {
    return (
      <div className="w-full h-32 flex items-center justify-center">
        <Icon
          type="far"
          name="spinner-third"
          className="text-on-disabled animate-spin text-2xl"
        />
      </div>
    );
  }

  if (_data.length === 0) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <>
      <ul className="px-2 min-h-32">
        {_data.map((_entity) => {
          if (searchType === searchTypesEnum.user) {
            const entity = _entity as UserType;
            const isSelected = selected.some((u) => u.id === entity.id);
            return (
              <li key={entity.id}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer",
                    isSelected && "bg-neutral-object-accent-quiet",
                  )}
                  onClick={(event) => {
                    if (
                      (globalThis?.getSelection()?.toString()?.length || 0) > 0
                    ) {
                      event.stopPropagation();
                      return;
                    }
                    handleSelectUser(entity, isSelected ? "remove" : "add");
                  }}
                >
                  <Avatar size="2xs">
                    <AvatarFallback color="action">
                      {entity.name ? `${entity.name.charAt(0)}` : ""}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {`${entity.name || entity.first_name} ${entity.last_name}`}
                    </span>
                    <span className="text-body-quiet font-normal truncate">
                      {entity.email || "-"}
                    </span>
                  </div>
                  {!isSelected && (
                    <div className="pl-3">
                      <Icon
                        type="far"
                        name="plus"
                        className="text-body text-base leading-3xl"
                      />
                    </div>
                  )}
                </button>
              </li>
            );
          }

          if (searchType === searchTypesEnum.roles) {
            const entity = _entity as RoleType;
            return (
              <li key={entity.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                  onClick={(event) => {
                    if (
                      (globalThis?.getSelection()?.toString()?.length || 0) > 0
                    ) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch("add", entity);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {entity.name}
                    </span>
                    <span className="text-body-quiet font-normal truncate text-xs">
                      {entity.description || "-"}
                    </span>
                  </div>
                  <div className="pl-3">
                    <Icon
                      type="far"
                      name="plus"
                      className="text-body text-base leading-3xl"
                    />
                  </div>
                </button>
              </li>
            );
          }

          if (searchType === searchTypesEnum.tags) {
            const entity = _entity as TagType;
            const isSelected = selected.some((tag) => tag.id === entity.id);
            const imageUrl =
              entity.files && entity.files.length > 0
                ? entity.files[0].url
                : null;
            return (
              <li key={entity.id}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer",
                    isSelected && "bg-neutral-object-accent-quiet",
                  )}
                  onClick={(event) => {
                    if (
                      (globalThis?.getSelection()?.toString()?.length || 0) > 0
                    ) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch(isSelected ? "remove" : "add", entity);
                    setIsOpen(false);
                  }}
                >
                  <div className=" w-full flex gap-2 items-center truncate">
                    <Avatar size="xs" shape="square">
                      <AvatarImage src={imageUrl || ""} alt={entity.name} />
                      <AvatarFallback
                        color="neutral"
                        shape="square"
                        type="icon"
                      >
                        <Icon type="fas" name="tag" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                      <span className="text-body font-semibold whitespace-nowrap">
                        {entity.name}
                      </span>
                      <p className="text-body-quiet font-normal truncate text-xs">
                        {entity.description || "-"}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="pl-3">
                      <Icon
                        type="far"
                        name="check"
                        className="text-body text-base leading-3xl"
                      />
                    </div>
                  )}
                </button>
              </li>
            );
          }

          if (
            searchType === searchTypesEnum.positions ||
            searchType === searchTypesEnum.institutions
          ) {
            const entity = _entity as CatalogSearchItem;
            return (
              <li key={entity.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 p-2 hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
                  onClick={(event) => {
                    if (
                      (globalThis?.getSelection()?.toString()?.length || 0) > 0
                    ) {
                      event.stopPropagation();
                      return;
                    }
                    onUpdateSearch("add", entity);
                    setIsOpen(false);
                  }}
                >
                  <div className="flex-1 flex flex-col items-start text-base leading-3xl truncate">
                    <span className="text-body font-semibold whitespace-nowrap">
                      {entity.name || entity.nombre || "-"}
                    </span>
                    <span className="text-body-quiet font-normal truncate text-xs">
                      {entity.description || "-"}
                    </span>
                  </div>
                  <div className="pl-3">
                    <Icon
                      type="far"
                      name="plus"
                      className="text-body text-base leading-3xl"
                    />
                  </div>
                </button>
              </li>
            );
          }

          return null;
        })}
      </ul>
      {hasNextPage && (
        <div
          ref={loaderRef}
          className="w-full flex items-center justify-center p-3"
        >
          <Icon
            type="far"
            name="spinner-third"
            className="text-on-disabled animate-spin text-xl"
          />
        </div>
      )}
    </>
  );
};
